import { AfterViewInit, Component, computed, DestroyRef, inject, input, linkedSignal, model, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatCalendar,
  DateRange as MatDateRange,
} from '@angular/material/datepicker';
import { ChitinCheckboxComponent } from '@shared/components/chitin/atoms/checkbox/checkbox.component';
import { ChitinFormInputComponent } from '@shared/components/chitin/atoms/form-input/form-input.component';
import { ChitinSelectComponent } from '@shared/components/chitin/atoms/select/select.component';
import { DateRange as MsgDateRange } from '@shared/components/chitin/molecules/daterange-picker/date-range';
import { getDisplayValueForDateRangePreset } from '@shared/components/data-view/filter-helpers';
import {
  AVAILABLE_DATERANGE_MODIFIERS,
  AVAILABLE_TIMEZONE_OPTIONS,
  DateRange,
  DATERANGE_MODIFIER_OPTIONS,
  Preset,
} from '@shared/components/daterange-picker/daterange-picker-const';
import { AppDateRangePickerDayjsAdapter, AppDateRangePickerFormats } from '@shared/components/daterange-picker/daterange-picker-formats';
import { DATERANGE_PRESET_TO_PRESET_ID_WITH_MODIFIER, DateRangePreset } from '@shared/models/date-range-preset';
import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import timezone from 'dayjs/plugin/timezone';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-daterange-picker',
  templateUrl: 'daterange-picker.component.html',
  styleUrl: './daterange-picker.component.scss',
  imports: [MatCalendar, ChitinCheckboxComponent, ChitinFormInputComponent, ChitinSelectComponent],
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DefaultMatCalendarRangeStrategy<Dayjs>,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: AppDateRangePickerFormats,
    },
    {
      provide: DateAdapter,
      useClass: AppDateRangePickerDayjsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
})
export class DaterangePickerComponent implements AfterViewInit {
  destroyRef = inject(DestroyRef);
  calendarLeft = viewChild<MatCalendar<Dayjs>>('calendarLeft');
  calendarRight = viewChild<MatCalendar<Dayjs>>('calendarRight');

  showTimezoneSelector = input(false);
  dateFormat = input<string>();
  forceUpdateCustomRangeTrigger = input<Observable<void>>();
  minDate = input<Date | Dayjs | string>();
  maxDate = input<Date | Dayjs | string>();
  formattedMinDate = computed(() => (this.minDate() ? dayjs(this.minDate()) : null));
  formattedMaxDate = computed(() => (this.maxDate() ? dayjs(this.maxDate()) : null));

  protected dateRange = model<MsgDateRange>();

  protected startDateInput = model<string | null>(null);
  protected endDateInput = model<string | null>(null);
  protected selectedTimezone = model<string>();

  protected startDate = computed<Dayjs | null>(() => (this.startDateInput() ? dayjs(this.startDateInput()).startOf('day') : null));
  protected endDate = computed<Dayjs | null>(() => (this.endDateInput() ? dayjs(this.endDateInput()).endOf('day') : null));

  protected selectedDateRange = computed<DateRange | null>(() => {
    const start = this.startDate();
    const end = this.endDate();
    return new MatDateRange(start, end);
  });

  protected selectedPreset = linkedSignal<Preset | null>(() => this.updateSelectedPreset());

  protected selectedThisModifier = signal(DATERANGE_MODIFIER_OPTIONS[0].value);
  protected selectedLastModifier = signal(DATERANGE_MODIFIER_OPTIONS[0].value);
  protected selectedToDateModifier = signal(DATERANGE_MODIFIER_OPTIONS[0].value);
  protected daterangeModifierOptions = signal(DATERANGE_MODIFIER_OPTIONS).asReadonly();
  protected timezoneOptions = signal(AVAILABLE_TIMEZONE_OPTIONS).asReadonly();

  protected hideThisSelection = computed(() => {
    // Hack to prevent UI switching "this" selection to "toDate" selection automatically, since
    // the date gets trimmed to fit within the min/max date settings.
    return this.formattedMaxDate()?.isSame(dayjs(), 'day');
  });

  constructor() {
    toObservable(this.dateRange)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newDateRange => {
        if (!newDateRange) return;
        if (newDateRange.preset) {
          const mappedPreset = DATERANGE_PRESET_TO_PRESET_ID_WITH_MODIFIER[newDateRange.preset];
          if (mappedPreset) {
            this.selectPreset(mappedPreset, undefined, false);
            return;
          }
        }
        this.startDateInput.set(dayjs(newDateRange.from).format('YYYY/MM/DD'));
        this.endDateInput.set(dayjs(newDateRange.to).format('YYYY/MM/DD'));
      });
  }

  ngAfterViewInit(): void {
    if (this.showTimezoneSelector()) {
      // Only override selected timezone if none is selected
      this.selectedTimezone.set(this.selectedTimezone() ?? this.getDefaultTimezone());
    }
    // Default daterange is "today" if none is selected
    if (this.startDateInput() === null || this.endDateInput() === null) this.selectPreset({ id: 'this', modifier: 'day' });
    const forceUpdateCustomRangeTrigger = this.forceUpdateCustomRangeTrigger();
    // This is required for cases where the parent element gets removed from DOM before the "custom date range" inputs get a chance to blur
    if (forceUpdateCustomRangeTrigger) {
      forceUpdateCustomRangeTrigger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.outputDaterange();
      });
    }
  }

  private getDefaultTimezone() {
    dayjs.extend(timezone);
    return dayjs.tz.guess();
  }

  protected selectPreset(preset: Preset, event?: MouseEvent, outputDaterange = true): void {
    if (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    if (!preset.modifier) {
      // Custom selected
      if (this.selectedPreset()?.id === 'custom') return;
      this.startDateInput.set(dayjs().subtract(1, 'day').startOf('day').format('YYYY/MM/DD'));
      this.endDateInput.set(dayjs().add(1, 'day').endOf('day').format('YYYY/MM/DD'));
      this.constrainDateRange();
      this.selectedPreset.set(preset);
      this.setViewToSelectedDateRange();
      if (outputDaterange) {
        this.outputDaterange();
        // Re-assert since `outputDaterange()` can rewrite the inputs
        this.selectedPreset.set(preset);
      }
      return;
    }
    this.startDateInput.set(
      dayjs()
        .subtract(preset.id === 'last' ? 1 : 0, preset.modifier as ManipulateType)
        .startOf(preset.modifier)
        .format('YYYY/MM/DD'),
    );
    if (preset.id === 'toDate') {
      this.endDateInput.set(dayjs().endOf('day').format('YYYY/MM/DD'));
    } else {
      this.endDateInput.set(
        dayjs()
          .subtract(preset.id === 'last' ? 1 : 0, preset.modifier as ManipulateType)
          .endOf(preset.modifier)
          .format('YYYY/MM/DD'),
      );
    }
    this.setViewToSelectedDateRange();
    if (outputDaterange) {
      this.outputDaterange();
    }
  }

  protected onManualDateInputChange(newDateInputValue: string | null) {
    if (!!newDateInputValue && dayjs(newDateInputValue).isValid()) this.setViewToSelectedDateRange();
  }

  private setViewToSelectedDateRange() {
    const calendarLeft = this.calendarLeft();
    const calendarRight = this.calendarRight();
    const startDate = this.startDate();
    const endDate = this.endDate();
    if (!!calendarLeft && !!startDate) calendarLeft.activeDate = startDate;
    if (!!calendarRight && !!endDate) calendarRight.activeDate = endDate;
  }

  protected selectedChange(value: Dayjs) {
    if (this.startDateInput() && this.endDateInput()) {
      // Clear previous value - a new daterange is being selected
      this.startDateInput.set(value.format('YYYY/MM/DD'));
      this.endDateInput.set(null);
      return;
    }
    if (this.startDateInput()) {
      // Start date has been set and we're now selecting the end date
      this.endDateInput.set(value.format('YYYY/MM/DD'));
      if (dayjs(this.startDateInput())?.isAfter(dayjs(this.endDateInput()))) {
        const oldStartDateInput = this.startDateInput();
        this.startDateInput.set(this.endDateInput());
        this.endDateInput.set(oldStartDateInput);
      }
      this.outputDaterange();
      return;
    }
  }

  protected outputDaterange() {
    let startDate = this.startDate();
    let endDate = this.endDate();
    if (!startDate || !endDate) return;

    if (!startDate.isValid()) this.startDateInput.set(dayjs().format('YYYY/MM/DD'));
    if (!endDate.isValid()) this.endDateInput.set(dayjs().format('YYYY/MM/DD'));

    this.constrainDateRange();
    this.setViewToSelectedDateRange();

    // We have to re-fetch the start and end dates, since they were potentially manipulated by this.constrainDateRange()
    startDate = this.startDate();
    endDate = this.endDate();
    if (!startDate || !endDate) return;

    const selectedPreset = this.selectedPreset();
    let msgPreset: DateRangePreset | null = null;
    if (selectedPreset !== null) {
      for (const [key, _preset] of Object.entries(DATERANGE_PRESET_TO_PRESET_ID_WITH_MODIFIER)) {
        if (!_preset) continue;
        if (_preset.id === selectedPreset.id && _preset.modifier === selectedPreset.modifier) {
          msgPreset = key as DateRangePreset;
        }
      }
    }
    if (selectedPreset === null && msgPreset === null) return;
    if (msgPreset === null) {
      this.dateRange.set({ from: startDate.toDate(), to: endDate.toDate() });
    } else {
      this.dateRange.set({
        from: startDate.toDate(),
        to: endDate.toDate(),
        preset: msgPreset,
        presetDisplayValue:
          getDisplayValueForDateRangePreset(this.startDate(), this.endDate(), msgPreset, this.dateFormat() ?? null) ?? undefined,
      });
    }
  }

  private constrainDateRange() {
    const startDate = this.startDate();
    const endDate = this.endDate();
    const minDate = this.formattedMinDate();
    const maxDate = this.formattedMaxDate();
    if (minDate && startDate && startDate.isBefore(minDate)) this.startDateInput.set(minDate.format('YYYY/MM/DD'));
    if (maxDate && endDate && endDate.isAfter(maxDate)) this.endDateInput.set(maxDate.format('YYYY/MM/DD'));
  }

  private updateSelectedPreset(): Preset | null {
    dayjs.extend(quarterOfYear);
    const today = dayjs();
    const selectedDateRange = this.selectedDateRange();
    if (!selectedDateRange?.end) return { id: 'custom' };
    if (!selectedDateRange.start) return null;
    for (const timeRange of AVAILABLE_DATERANGE_MODIFIERS) {
      if (selectedDateRange.start.isSame(today.startOf(timeRange))) {
        if (selectedDateRange.end.isSame(today.endOf(timeRange))) return { id: 'this', modifier: timeRange };
        if (selectedDateRange.end.isSame(today.endOf('day'))) return { id: 'toDate', modifier: timeRange };
      }
      if (
        selectedDateRange.start.isSame(today.subtract(1, timeRange as ManipulateType).startOf(timeRange)) &&
        selectedDateRange.end.isSame(today.subtract(1, timeRange as ManipulateType).endOf(timeRange))
      ) {
        return { id: 'last', modifier: timeRange };
      }
    }
    return { id: 'custom' };
  }
}
